import { axiosInstance, printError, useSearch, useUtilities } from "helpers";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getAllRoles,
  addNewRole,
  updateSingleRole,
  deleteSingleRole,
  getAllDefaultRoles,
  deleteSingleDefaultRole,
  addNewDefaultRole,
} from "redux/reducers/usersModule/roles";
import { useEffect } from "react";
import { t } from "i18next";

async function getRoles() {
  return axiosInstance
    .get("allroles", {
      params: {
        api_key: localStorage.getItem("apikey"),
        added_by: localStorage.getItem("user_id"),
      },
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

function useGetRoles() {
  return useQuery(["roles"], getRoles);
}

export function useRoles() {
  const { data, isLoading, error } = useGetRoles();
  const {
    dispatch,
    roles: { roles, loaded },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) {
      dispatch(getAllRoles(data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded]);
  return { data: roles, isLoading: isLoading || !loaded, error };
}

export async function addRole({ data, callback }) {
  return axiosInstance
    .post("addrole", {
      api_key: localStorage.getItem("apikey"),
      added_by: localStorage.getItem("user_id"),
      ...data,
    })
    .then((res) => {
      callback && res.data.data.id && callback(res.data.data.id);
      return res.data;
    });
}

export function useAddRole(redirect, updatedDefault = false) {
  const { web = null } = useSearch();
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addRole, {
    onSuccess: (data) => {
      if (data?.status_code === 200) {
        if (updatedDefault) dispatch(addNewDefaultRole(data.data));
        else dispatch(addNewRole(data.data));
        notification({
          type: "success",
          message: t("users.roles.api.notification.roleAdded"),
        });
        if (redirect)
          navigate(`${redirect}${data.data.id}${web ? `?web=${web}` : ""}`);
      } else {
        notification({
          type: "error",
          message:
            data?.message ||
            t("common.error.somethingWentWrong"),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message:
          err?.response?.data?.message || t("common.error.somethingWentWrong"),
      });
    },
  });
}

export async function updateRole(data) {
  return axiosInstance
    .post("updaterole", {
      api_key: localStorage.getItem("apikey"),
      ...data,
    })
    .then((res) => res.data)
}

export function useUpdateRole() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateRole, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateSingleRole(data.data));
        notification({
          type: "success",
          message: t("users.roles.api.notification.roleUpdated"),
        });
      } else {
        notification({
          type: "error",
          message: data?.message || t("common.error.somethingWentWrong"),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err?.response?.data?.message || t("common.error.somethingWentWrong"),
      });
    },
  });
}

async function deleteRole(id) {
  return axiosInstance
    .post("delrole", {
      api_key: localStorage.getItem("apikey"),
      role_id: id,
    })
    .then((res) => ({ ...res.data, id }))
}

export function useDeleteRole(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteRole, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteSingleRole(data.id));
        notification({
          type: "success",
          message: t("users.roles.api.notification.roleDeleted"),
        });
        if (redirect) navigate(redirect);
      } else {
        notification({
          type: "error",
          message: data?.message || t("common.error.somethingWentWrong"),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err?.response?.data?.message || t("common.error.somethingWentWrong"),
      });
    },
  });
}

async function getDefaultRoles(website_id) {
  return axiosInstance
    .post("getdefaultrole", {
      api_key: localStorage.getItem("apikey"),
      website_id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetDefaultRoles() {
  const { web = null } = useSearch();
  return useQuery(["defaultRoles", web], () => getDefaultRoles(web));
}

export function useDefaultRoles() {
  const { web = null } = useSearch();
  const { data, isLoading, error } = useGetDefaultRoles();
  const {
    dispatch,
    roles: {
      defaultTemplate: { roles, website_id },
    },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || web === website_id) return;
    if (data) {
      dispatch(
        getAllDefaultRoles({
          data: data.data,
          website_id: web,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, website_id]);

  return {
    data: roles,
    isLoading: isLoading || website_id === undefined,
    error,
  };
}

async function addDefaultRole(data) {
  return axiosInstance
    .post("adddefaulttemplaterole", {
      api_key: localStorage.getItem("apikey"),
      ...data,
    })
    .then((res) => res.data)
}

export function useAddDefaultRole(notify = true) {
  const { dispatch, notification } = useUtilities();
  return useMutation(addDefaultRole, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addNewDefaultRole(data.data));
        if (notify) {
          notification({
            type: "success",
            message: t(
              "users.roles.api.notification.roleAddedToDefaultTemplate"
            ),
          });
        }
      } else {
        notification({
          type: "error",
          message: printError(data.message || data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err?.response?.data?.message),
      });
    },
  });
}

async function deleteDefaultRole(data) {
  return axiosInstance
    .post("defaultroleremove", {
      api_key: localStorage.getItem("apikey"),
      ...data,
    })
    .then((res) => ({ ...res.data, id: data.id }))
}

export function useDeleteDefaultRole() {
  const { dispatch, notification } = useUtilities();
  return useMutation(deleteDefaultRole, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteSingleDefaultRole(data.id));
        notification({
          type: "success",
          message: t(
            "users.roles.api.notification.roleRemovedToDefaultTemplate"
          ),
        });
      } else {
        notification({
          type: "error",
          message: printError(data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err?.response?.data?.message),
      });
    },
  });
}

export async function checkRoleBeforeDelete(role_id) {
  return axiosInstance
    .post("checkbeforedelrole", {
      api_key: localStorage.getItem("apikey"),
      role_id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useCheckRoleBeforeDelete() {
  return useMutation(checkRoleBeforeDelete);
}
